import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const { count } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/aktuality", label: t.nav.news },
    { href: "/koncerty", label: t.nav.concerts },
    { href: "/merch", label: t.nav.merch },
    { href: "/o-nas", label: t.nav.about },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: "rgba(5,5,5,0.85)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <span style={{
            fontFamily: "Unbounded, sans-serif",
            fontWeight: 900,
            fontSize: "1.2rem",
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: "#FF005A",
          }}>REBELKY</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-${link.href.replace("/", "")}`}
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontSize: "0.65rem",
                fontWeight: 600,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: isActive(link.href) ? "#FF005A" : "#A1A1AA",
                transition: "color 0.2s",
              }}
              className="hover:text-white"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div className="flex items-center gap-1" data-testid="lang-switcher">
            <button
              data-testid="lang-cz"
              onClick={() => setLang("cz")}
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: lang === "cz" ? "#FF005A" : "#52525B",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                transition: "color 0.2s",
              }}
            >CZ</button>
            <span style={{ color: "#52525B", fontSize: "0.6rem" }}>|</span>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: lang === "en" ? "#FF005A" : "#52525B",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                transition: "color 0.2s",
              }}
            >EN</button>
          </div>

          {/* Cart */}
          <Link to="/kosik" data-testid="cart-icon" className="relative" style={{ color: "#A1A1AA" }}>
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 flex items-center justify-center w-4 h-4 rounded-full text-white"
                style={{ background: "#FF005A", fontSize: "0.55rem", fontWeight: 700, fontFamily: "Unbounded, sans-serif" }}
              >
                {count}
              </span>
            )}
          </Link>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ color: "#A1A1AA", background: "none", border: "none", cursor: "pointer" }}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden flex flex-col"
          style={{
            background: "rgba(5,5,5,0.98)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "1.5rem",
            gap: "1rem",
          }}
        >
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isActive(link.href) ? "#FF005A" : "#FFFFFF",
                textDecoration: "none",
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
